import React, { useCallback, useEffect, useRef, useState } from 'react';
import { invoicesService } from '../../services/api';
import { fmtCurrency, fmtDate, fmtDateTime, fmtTime, getTodayStr, parseDecimalBr } from '../../utils/formatters';
import { TONE_BADGE } from '../../utils/statusColors';
import { prazoDaPromessa } from '../../utils/filaDeCobranca';
import { campo, mensagemDoErroDeCarga } from '../../design';
import { useAviso } from '../shared/Aviso';

/**
 * O painel CONTATOS da fatura (G1, plano de cobrança de 30/10/2026): o histórico do que o cliente disse e o formulário
 * para registrar uma conversa, com a promessa de pagamento. Mora na gaveta da aba Cobrança e no modal da fatura.
 * Prévia: https://claude.ai/artifact/E1gGoqTdCgJerRZUZb4nUA
 *
 * Decisões do Heitor que a tela diz (30/10/2026):
 * - a promessa pausa a cobrança até a data + 2 dias úteis, e a frase mostra AS DUAS datas;
 * - promessa nova sobre uma pendente marca a anterior como não cumprida (o backend faz; a tela avisa antes);
 * - o placar conta PROMESSAS, inclusive de faturas pagas depois; por isso a frase não diz "faturas";
 * - ADMIN e EDITOR registram (`podeRegistrar`); vendedor e leitor só leem, sem o formulário.
 */

export const CANAIS: Array<{ valor: string; rotulo: string }> = [
  { valor: 'telefone', rotulo: 'Telefone' },
  { valor: 'whatsapp', rotulo: 'WhatsApp' },
  { valor: 'email', rotulo: 'E-mail' },
  { valor: 'presencial', rotulo: 'Presencial' },
  { valor: 'outro', rotulo: 'Outro' },
];
const rotuloDoCanal = (c: string) => CANAIS.find((x) => x.valor === c)?.rotulo || c;

const DESFECHO: Record<string, { texto: string; cls: string }> = {
  pendente: { texto: 'pendente', cls: TONE_BADGE.warning },
  cumprida: { texto: 'cumprida', cls: TONE_BADGE.success },
  nao_cumprida: { texto: 'não cumprida', cls: TONE_BADGE.danger },
};

interface Promessa { date: string; amount: number | null; status: string | null; holdUntil?: string | null }
interface Contato { id: number; contactedAt: string; channel: string; authorName: string; clientSaid: string | null; promise: Promessa | null }
interface Placar { total: number; cumpridas: number; naoCumpridas: number; pendentes: number; ultimaNaoCumprida: string | null }

/** Data e hora do formulário (BRT, sem horário de verão desde 2019) → instante ISO. */
const instante = (data: string, hora: string) => new Date(`${data}T${hora || '00:00'}:00-03:00`).toISOString();

export default function ContatosDaFatura({ invoiceId, clientId, podeRegistrar, aoMudar }: {
  invoiceId: number;
  clientId: number;
  podeRegistrar: boolean;
  /** Chamado depois de gravar: a fila recarrega, porque a promessa muda o grupo da fatura. */
  aoMudar?: () => void;
}) {
  const avisar = useAviso();
  const [contatos, setContatos] = useState<Contato[] | null>(null);
  const [atual, setAtual] = useState<{ id: number; date: string; pausaAte: string | null } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [placar, setPlacar] = useState<Placar | null>(null);
  const [erroPlacar, setErroPlacar] = useState<string | null>(null);

  const [data, setData] = useState(getTodayStr());
  const [hora, setHora] = useState(fmtTime(Date.now()));
  const [canal, setCanal] = useState('telefone');
  const [disse, setDisse] = useState('');
  const [prometeu, setPrometeu] = useState(false);
  const [dataPrometida, setDataPrometida] = useState('');
  const [valorPrometido, setValorPrometido] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [carimbando, setCarimbando] = useState<number | null>(null);

  // ⚠️ Cada leitura confere se a fatura ainda é a da tela: a gaveta troca de fatura sem desmontar, e a resposta atrasada
  // da anterior não pode aparecer sob esta (tela com recorte carrega duas perguntas).
  const daTela = useRef(invoiceId);
  daTela.current = invoiceId;
  const carregar = useCallback(async () => {
    const minha = invoiceId;
    setErro(null);
    try {
      const r = await invoicesService.getContacts(invoiceId);
      if (minha !== daTela.current) return;
      setContatos(Array.isArray(r?.data) ? r.data : []);
      setAtual(r?.promiseAtual ? { id: r.promiseAtual.id, date: r.promiseAtual.date, pausaAte: r.promiseAtual.pausaAte || null } : null);
    } catch (e) {
      if (minha === daTela.current) setErro(mensagemDoErroDeCarga(e));
    }
    try {
      setErroPlacar(null);
      const p = await invoicesService.promiseStats(clientId);
      if (minha !== daTela.current) return;
      setPlacar(p);
    } catch (e) {
      if (minha === daTela.current) setErroPlacar(mensagemDoErroDeCarga(e));
    }
  }, [invoiceId, clientId]);

  useEffect(() => {
    setContatos(null);
    setAtual(null);
    setPlacar(null);
    carregar();
  }, [carregar]);

  const valor = valorPrometido.trim() ? parseDecimalBr(valorPrometido) : null;
  const valorInvalido = valorPrometido.trim() !== '' && (valor === null || valor <= 0);
  const prazo = prometeu ? prazoDaPromessa(dataPrometida) : null;
  const faltaData = prometeu && !dataPrometida;
  const vazio = !disse.trim() && !prometeu;

  const salvar = async () => {
    if (salvando || faltaData || valorInvalido) return;
    setSalvando(true);
    try {
      const r = await invoicesService.registerContact(invoiceId, {
        contactedAt: instante(data, hora),
        channel: canal,
        clientSaid: disse.trim() || undefined,
        ...(prometeu ? { promisedDate: dataPrometida, ...(valor !== null ? { promisedAmount: valor } : {}) } : {}),
      });
      avisar(
        r?.dunningPausedUntil
          ? `Contato registrado. A cobrança pausa até ${fmtDate(r.dunningPausedUntil)}.`
          : 'Contato registrado.',
        'success',
      );
      setDisse('');
      setPrometeu(false);
      setDataPrometida('');
      setValorPrometido('');
      await carregar();
      aoMudar?.();
    } catch (e) {
      avisar(`Não registrou o contato: ${mensagemDoErroDeCarga(e)}`, 'error');
    } finally {
      setSalvando(false);
    }
  };

  const carimbar = async (id: number, status: 'cumprida' | 'nao_cumprida') => {
    setCarimbando(id);
    try {
      await invoicesService.setPromiseOutcome(id, status);
      await carregar();
      aoMudar?.();
    } catch (e) {
      avisar(`Não marcou a promessa: ${mensagemDoErroDeCarga(e)}`, 'error');
    } finally {
      setCarimbando(null);
    }
  };

  const botao = 'px-3 py-1.5 rounded-lg text-sm border border-central-borda bg-central-cartao text-central-texto hover:bg-central-elevado disabled:opacity-40';

  return (
    <div className='space-y-4' data-contatos-da-fatura={invoiceId}>
      <p className='text-sm text-central-texto-fraco' data-placar>
        {erroPlacar
          ? <span className='text-amber-300'>O placar do cliente não carregou: {erroPlacar}</span>
          : !placar
            ? 'Lendo o placar do cliente…'
            : placar.total === 0
              ? 'Este cliente ainda não fez nenhuma promessa.'
              : <>Promessas deste cliente: <b className='text-central-texto'>{placar.cumpridas} {placar.cumpridas === 1 ? 'cumprida' : 'cumpridas'}</b>
                  {' · '}<b className={placar.naoCumpridas ? 'text-red-400' : 'text-central-texto'}>{placar.naoCumpridas} não {placar.naoCumpridas === 1 ? 'cumprida' : 'cumpridas'}</b>
                  {placar.ultimaNaoCumprida ? ` (a última em ${fmtDate(placar.ultimaNaoCumprida)})` : ''}
                  {' · '}{placar.pendentes} {placar.pendentes === 1 ? 'pendente' : 'pendentes'}</>}
      </p>

      <div className={`grid gap-5 ${podeRegistrar ? 'lg:grid-cols-2' : ''}`}>
        {podeRegistrar && (
          <form className='space-y-3' onSubmit={(e) => { e.preventDefault(); salvar(); }} aria-label='Registrar contato'>
            <div className='text-xs uppercase tracking-wider text-central-texto-fraco'>Registrar contato</div>
            <div className='flex gap-2'>
              <label className='block text-xs text-central-texto-fraco'>Data
                <input type='date' value={data} max={getTodayStr()} onChange={(e) => setData(e.target.value)} className={`mt-1 block px-3 py-1.5 rounded-lg text-sm ${campo}`} />
              </label>
              <label className='block text-xs text-central-texto-fraco'>Hora
                <input type='time' value={hora} onChange={(e) => setHora(e.target.value)} className={`mt-1 block px-3 py-1.5 rounded-lg text-sm ${campo}`} />
              </label>
            </div>
            <div role='radiogroup' aria-label='Canal' className='flex flex-wrap gap-1.5'>
              {CANAIS.map((c) => (
                <button
                  key={c.valor}
                  type='button'
                  role='radio'
                  aria-checked={canal === c.valor}
                  onClick={() => setCanal(c.valor)}
                  className={`px-3 py-1 rounded-full text-xs border ${canal === c.valor ? 'border-primary text-central-texto font-medium bg-primary/10' : 'border-central-borda text-central-texto-fraco hover:text-central-texto'}`}
                >
                  {c.rotulo}
                </button>
              ))}
            </div>
            <label className='block text-xs text-central-texto-fraco'>O que o cliente disse
              <textarea value={disse} onChange={(e) => setDisse(e.target.value)} rows={3} placeholder='Ex.: não atendeu; ou "o financeiro paga sexta, depois do fechamento".' className={`mt-1 w-full px-3 py-2 rounded-lg text-sm ${campo}`} />
            </label>
            <label className='flex items-center gap-2 text-sm text-central-texto'>
              <input type='checkbox' checked={prometeu} onChange={(e) => setPrometeu(e.target.checked)} />
              Prometeu pagar
            </label>
            {prometeu && (
              <div className='space-y-2 pl-6'>
                <div className='flex gap-2'>
                  <label className='block text-xs text-central-texto-fraco'>Data prometida
                    <input type='date' value={dataPrometida} onChange={(e) => setDataPrometida(e.target.value)} className={`mt-1 block px-3 py-1.5 rounded-lg text-sm ${campo}`} />
                  </label>
                  <label className='block text-xs text-central-texto-fraco'>Valor (opcional)
                    <input type='text' inputMode='decimal' value={valorPrometido} onChange={(e) => setValorPrometido(e.target.value)} placeholder='R$' className={`mt-1 block w-32 px-3 py-1.5 rounded-lg text-sm ${campo}`} />
                  </label>
                </div>
                {valorInvalido && <p className='text-xs text-red-400'>Valor inválido.</p>}
                {prazo && (
                  <p className='text-xs text-central-texto-fraco' data-prazo-da-promessa>
                    A cobrança pausa até <b className='text-central-texto'>{fmtDate(prazo)}</b>: {fmtDate(dataPrometida)} mais 2 dias úteis para o TED ou o boleto compensar. Feriados não entram na conta.
                  </p>
                )}
                {atual && (
                  <p role='note' className='text-xs border-l-[3px] border-l-amber-500 bg-amber-500/10 px-3 py-2 rounded' data-promessa-anterior>
                    Esta fatura já tem uma promessa pendente para {fmtDate(atual.date)}. Ao salvar, ela passa a contar como <b>não cumprida</b> e fica no histórico.
                  </p>
                )}
              </div>
            )}
            <button type='submit' className={botao} disabled={salvando || vazio || faltaData || valorInvalido}>
              {salvando ? 'Salvando…' : 'Salvar contato'}
            </button>
          </form>
        )}

        <div>
          <div className='text-xs uppercase tracking-wider text-central-texto-fraco mb-2'>Histórico, mais novo primeiro</div>
          {erro ? (
            <div role='alert' className={`px-3 py-2 rounded-lg text-sm ${TONE_BADGE.danger}`}>
              Não consegui ler os contatos desta fatura: {erro}. Isso não quer dizer que não há nenhum.
              <button type='button' onClick={carregar} className='ml-2 underline'>Tentar de novo</button>
            </div>
          ) : contatos === null ? (
            <p className='text-sm text-central-texto-fraco'>Lendo os contatos…</p>
          ) : contatos.length === 0 ? (
            <p className='text-sm text-central-texto-fraco' data-sem-contatos>Nenhum contato registrado nesta fatura.</p>
          ) : (
            <ol className='border-l-2 border-central-borda pl-3 space-y-3'>
              {contatos.map((c) => {
                const d = c.promise?.status ? DESFECHO[c.promise.status] : null;
                return (
                  <li key={c.id} data-contato={c.id}>
                    <div className='text-sm'><b className='text-central-texto'>{fmtDateTime(c.contactedAt)}</b> <span className='text-central-texto-fraco'>· {rotuloDoCanal(c.channel)} · {c.authorName}</span></div>
                    {c.clientSaid && <div className='text-sm text-central-texto-fraco whitespace-pre-line'>{c.clientSaid}</div>}
                    {c.promise && (
                      <div className='text-sm text-central-texto flex flex-wrap items-center gap-2 mt-0.5'>
                        <span>Promessa {fmtDate(c.promise.date)} · {c.promise.amount != null ? fmtCurrency(c.promise.amount) : 'sem valor'}</span>
                        {d && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${d.cls}`}>{d.texto}</span>}
                        {c.promise.status === 'pendente' && c.promise.holdUntil && (
                          <span className='text-xs text-central-texto-fraco'>cobrança volta em {fmtDate(c.promise.holdUntil)}</span>
                        )}
                        {podeRegistrar && c.promise.status === 'pendente' && (
                          <span className='flex gap-1'>
                            <button type='button' disabled={carimbando === c.id} onClick={() => carimbar(c.id, 'cumprida')} className='text-xs underline text-central-texto-fraco hover:text-central-texto'>Marcar cumprida</button>
                            <button type='button' disabled={carimbando === c.id} onClick={() => carimbar(c.id, 'nao_cumprida')} className='text-xs underline text-central-texto-fraco hover:text-central-texto'>Marcar não cumprida</button>
                          </span>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
          {!podeRegistrar && <p className='text-xs text-central-texto-fraco mt-3'>Só admin e editor registram contato.</p>}
        </div>
      </div>
    </div>
  );
}
